import { forkJoin } from 'rxjs';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';

// Shared Components and Helpers
import { Alert } from '../../../../shared/alert.model';
import { MessageType } from '../../../../shared/messageType';
import { getAlertMessage } from '../../../../shared/api-loaders';
import { Select2Option } from '../../../../shared/select2-option';
import { CascadeManager } from '../../../../shared/cascade-manager';
import { getDefaultDataTableDefinitionLocalProcessing } from '../../../../shared/DataTablesHelper';

// Services
import { ModalService } from '../../../../shared/modal.service';
import { MessageService } from '../../../../shared/message.service';
import { SecurityService } from '../../../../shared/security.service';
import { ProfileSyncService } from '../../../services/profile-sync.service';
import { CustomerService } from '../../../../ect-feature/services/customer.service';
import { KeycloakMigrationService } from "../../../services/keycloak-migration.service";
// Models
import { MigrationUser } from '../../../model/migration-user';
import { RolesDisciplines } from '../../../../users/models/user-detail-options';
import { KeycloakImportData } from '../../../../conversions-feature/model/KeycloakImportData';

// Component
import { KeycloakImportConfirmationDialogComponent } from '../keycloak-import-confirmation-dialog/keycloak-import-confirmation-dialog.component';

@Component({
    selector: 're-keycloak-list',
    templateUrl: './keycloak-list.component.html',
    styleUrls: ['./keycloak-list.component.scss'],
    standalone: false
})
export class KeycloakListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild("form")
  form: NgForm;

  // Data
  referenceData: RolesDisciplines[];
  // Table Data
  unfilteredData: MigrationUser[];
  results: MigrationUser[] = [];
  dataTable: DataTables.Api;

  // Dropdowns
  customer: number = null;
  environment: number = null;
  corporation: number = null;

  customers: Select2Option[] = [];
  environments: Select2Option[] = [];
  corporations: Select2Option[] = [];

  // Miscellaneous
  onlyErrors = false;
  inactiveUsers = false;
  numberOfErrors: number;
  errorPercentage: number;
  isKeycloakImportUser: boolean;

  // Helpers
  alert: Alert;
  cascadeManager = new CascadeManager(this);

  get exportToCsvButton(): Element {
    return this.elementRef.nativeElement.querySelector(".button-container .buttons-csv");
  }

  constructor(private elementRef: ElementRef, private renderer: Renderer2, private route: ActivatedRoute, private router: Router,
              private messageService: MessageService,
              private customerService: CustomerService, private keycloakMigrationService: KeycloakMigrationService,
              private profileSyncService: ProfileSyncService, private modalService: ModalService, private securityService: SecurityService) {
    this.cascadeManager.defaultResetAction = () => { this.alert = undefined; };
    this.cascadeManager.addEvent({ event: this.customerChanged,    reset: () => { this.customers = []; this.customer = null; this.referenceData = []; } });
    this.cascadeManager.addEvent({ event: this.environmentChanged, reset: () => { this.environments = []; this.environment = null; } });
    this.cascadeManager.addEvent({ event: this.corporationChanged, reset: () => { this.corporations = []; this.corporation = null; } });
    this.cascadeManager.addEvent({ event: null, reset: () => { this.unfilteredData = null; } });
  }

  async ngOnInit() {
    if (await this.securityService.isAuthenticated()) {
      this.isKeycloakImportUser = await this.securityService.isKeycloakImportUser().toPromise();
    }
    this.load();
    this.configDataTable();
  }

  // Load customers only
  // Environments will be populated from reference data API when customer is selected
  private async load(): Promise<void> {
    this.showSpinner();

    this.customerService.getCustomersByStack().subscribe(customers => {
      this.customers = customers.map(c => { return { data: c.accountId, id: c.id.toString(), text: c.accountName } });
      this.hideSpinner();
    }, error => {
      this.hideSpinner();
      this.alert = getAlertMessage(error);
    });

  }

  ngAfterViewInit(): void {
    this.reset();
  }

  export() {  }

  reset() {
    this.form.resetForm({});
    this.environments = [];
    this.corporations = [];
    this.referenceData = [];
    this.results = null;
    this.unfilteredData = null;
    this.dataTable?.clear();
    this.dataTable?.draw();
    this.inactiveUsers = false;
    this.cascadeManager.runFirstEvent();
    this.calculateNumberOfErrors();
    if((<HTMLInputElement>$("#templatesTree .treeview-header .row-filter input")[ 0 ])) {
      (<HTMLInputElement>$("#templatesTree .treeview-header .row-filter input")[ 0 ]).value = "";
    }
  }

  customerChanged($event): void {
    this.cascadeManager.resetDependencies(this.customerChanged);
    this.inactiveUsers = false;

    if (this.customer) {
      this.showSpinner();
      let crmId = this.customers.firstOrDefault(x => +x.id == this.customer).data;

      this.keycloakMigrationService.getReferenceData(crmId).subscribe(x => {
        this.referenceData = x;

        const uniqueEnvironments = this.referenceData
          .map<Select2Option>(e => ({ id: e.configId.toString(), text: e.configName }))
          .filter((env, index, self) =>
            index === self.findIndex((t) => t.id === env.id)
          );

        this.environments = [
          { id: "", text: 'Not Selected' },
          ...uniqueEnvironments
        ];

        this.environment = null;

        this.cascadeManager.runNextDependendentEvent(this.customerChanged);
        this.hideSpinner();
      }, error => {
        this.hideSpinner();
        this.alert = getAlertMessage(error);
      });
    } else {
      this.hideSpinner();
      this.cascadeManager.runNextDependendentEvent(this.customerChanged);
    }
  }

  environmentChanged($event): void {
    this.cascadeManager.resetDependencies(this.environmentChanged);
    this.inactiveUsers = false;

    if (this.environment) {
      this.showSpinner();
      this.profileSyncService.getCorporations(this.environment).subscribe(x => {
        const corporationOptions = x.map<Select2Option>(e => ({ data: e.environmentID, id: e.corporationID.toString(), text: e.corpName }));

        this.corporations = [
          { id: "", text: 'Not Selected', data: null },
          ...corporationOptions
        ];

        this.corporation = null;

        this.cascadeManager.runNextDependendentEvent(this.environmentChanged);
        this.hideSpinner();
      }, err => {
        this.alert = getAlertMessage(err);
        this.hideSpinner();
      });
    } else {
      this.hideSpinner();
      this.cascadeManager.runNextDependendentEvent(this.environmentChanged);
    }
  }

  getCurrentEnvironment(): RolesDisciplines | null {
    let currentEnvironment = this.referenceData.firstOrDefault(x => +x.configId == this.environment);
    if (currentEnvironment == null) {
      console.error(`Environment ${this.environment} not found.`);
      return null;
    }
    return currentEnvironment;
  }

  corporationChanged($event): void {
    this.cascadeManager.resetDependencies(this.corporationChanged);
    this.inactiveUsers = false;

    if (this.corporation) {
      this.showSpinner();
      let currentEnvironment = this.getCurrentEnvironment();
      this.hideSpinner();
      if (currentEnvironment == null) return;
    }

    this.hideSpinner();
    this.cascadeManager.runNextDependendentEvent(this.corporationChanged);
  }

  onlyErrorsChanged($event) {
    this.applyExtraFilter();
  }

  applyExtraFilter() {
    if (this.onlyErrors)
      this.results = this.unfilteredData.filter(x => x.hasErrors == this.onlyErrors)
    else
      this.results = this.unfilteredData;

    this.dataTable.clear();
    this.dataTable.rows.add(this.results);
    this.dataTable.draw();
  }

  enableDisableExportButton() {
    if (this.dataTable.page.info().recordsDisplay == 0)
      this.disableExportButton();
    else
      this.enableExportButton();
  }

  disableExportButton() {
    this.renderer.setAttribute(this.exportToCsvButton, "disabled", "true");
  }

  enableExportButton() {
    this.renderer.removeAttribute(this.exportToCsvButton, "disabled");
  }

  configDataTable() {
    window['currentComponent'] = this;
    this.dataTable = getDefaultDataTableDefinitionLocalProcessing("#resultsDataTable", {
      buttons: [
        {
          extend: 'csv', text: 'Export to CSV', className:'btn btn-primary'
        }
      ],
      columns: [
        {
          title: 'First Name', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.firstName}`;
          }
        },
        {
          title: 'Middle Initial', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.middleInitial ?? ""}`;
          }
        },
        {
          title: 'Last Name', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.lastName}`;
          }
        },
        {
          title: 'User Name', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.userName}`;
          }
        },
        {
          title: 'Email', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.email ?? ""}`;
          }
        },
        {
          title: 'Keycloak SSOId', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.exportedUserId}`;
          }
        },
        {
          title: 'Notes', render: (data: any, type: any, r: MigrationUser) => {
            return `${r.notes}`;
          }
        },
      ]
    });

    new $.fn.dataTable[ 'FixedHeader' ](this.dataTable);
    this.dataTable.buttons().container().appendTo($('.button-container'));

    this.dataTable.on('search.dt', () => { this.enableDisableExportButton(); });
  }

  run() {
    this.alert = undefined;
    this.results = [];
    this.showSpinner();

    let environmentId = +this.corporations.firstOrDefault(x => +x.id == this.corporation).data;
    let crmID = this.customers.firstOrDefault(x => +x.id == this.customer).data;
    this.keycloakMigrationService.getUsers(crmID, environmentId, this.corporation, this.inactiveUsers).subscribe(data => {
      this.unfilteredData = data;
      this.applyExtraFilter();
      this.calculateNumberOfErrors();
      this.enableDisableExportButton();
      this.hideSpinner();
    }, error => {
      this.hideSpinner();
      this.alert = getAlertMessage(error);
    })
  }

  async import() {
    let dialog = this.modalService.create<KeycloakImportConfirmationDialogComponent>(KeycloakImportConfirmationDialogComponent);
    dialog.component.numberOfUsers = this.unfilteredData.length;
    dialog.component.crmID = this.customers.firstOrDefault(x => +x.id == this.customer).data;

    let result = await dialog.modalRef.result;
    if (result) {
      this.importUsers(dialog.component.domain, dialog.component.crmID);
    }
  }

  importUsers(domain:string, crmID:string) {
    this.showSpinner();
    let environmentId = +this.corporations.firstOrDefault(x => +x.id == this.corporation).data;
    let importKeycloakUsers : KeycloakImportData = {
      configId : this.environment, // environment is bound to the configuration.id from /api/environment/configurations
      environmentId : environmentId,
      corpId : this.corporation,
      crmId : crmID,
      emailDomain : domain,
      environmentName: this.environments.firstOrDefault(x => +x.id == this.environment).text,
      corporationName: this.corporations.firstOrDefault(x => +x.id == this.corporation).text,
      inactiveUsers: this.inactiveUsers
    };
    this.keycloakMigrationService.import(importKeycloakUsers).subscribe(x => {
      this.hideSpinner();
      this.back();
    }, error => {
      this.hideSpinner();
      this.alert = getAlertMessage(error);
    })
  }

  private calculateNumberOfErrors() {
    let errorCount = 0;
    let results = this.unfilteredData;
    if (!results)
      return;

    results.forEach(x => {
      if (x.hasErrors)
        errorCount++;
    });
    this.numberOfErrors = errorCount;
    this.errorPercentage = 0;
    if (results.length > 0) {
      this.errorPercentage = errorCount / results.length * 100;
    }
  }

  private showSpinner(): void {
    this.messageService.send(MessageType.ProcessingOn);
  }

  private hideSpinner(): void {
    this.messageService.send(MessageType.ProcessingOff);
  }

  back() {
    this.router.navigate([".."], { relativeTo: this.route })
  }

  ngOnDestroy(): void {
    delete window['currentComponent'];
    this.dataTable.destroy();
  }
}
